import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { BooksBlocks } from './books/BooksBlocks';
import {
  normalizeCatalogBlockType,
  templateForBlockType,
  type CatalogBlockType,
  type BlockTemplateKind,
} from '../content/blockCatalog';
import './books/books-blocks.css';

export type BlockPayload = {
  id: string;
  blockType: string;
  payload: Record<string, unknown>;
  perspectives: string[];
};

type DisplayMode = 'simple' | 'full' | 'compare' | 'study';
type PerspectiveMode = 'all' | 'none' | string;

function depthRank(mode: DisplayMode): number {
  switch (mode) {
    case 'simple':
      return 0;
    case 'full':
      return 1;
    case 'compare':
      return 1;
    case 'study':
      return 2;
    default:
      return 1;
  }
}

function normalizeAudience(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().toLowerCase();
  return trimmed || null;
}

/** Inline **bold**, *italic*, and `code` within a single line/paragraph (no nested markers). */
export function formatInline(text: string): ReactNode {
  const nodes: ReactNode[] = [];
  const re = /\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  // eslint-disable-next-line no-cond-assign
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[1] != null) nodes.push(<strong key={k++}>{m[1]}</strong>);
    else if (m[2] != null) nodes.push(<em key={k++}>{m[2]}</em>);
    else if (m[3] != null) nodes.push(<code key={k++}>{m[3]}</code>);
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes.length ? <>{nodes}</> : text;
}

function MdParagraph({ children }: { children: string }) {
  return <p>{formatInline(children)}</p>;
}

/** Shared markdown-ish renderer for module blocks and legacy sections. */
export function Md({ children }: { children: string }) {
  const trimmed = children.trim();
  if (!trimmed) return null;
  return (
    <div className="md-body">
      {trimmed.split(/\n\n+/).map((para, i) => (
        <MdParagraph key={i}>{para}</MdParagraph>
      ))}
    </div>
  );
}

function getString(payload: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const v = payload[key];
    if (typeof v === 'string' && v.trim()) return v;
  }
  return '';
}

function getArray(payload: Record<string, unknown>, ...keys: string[]): string[] {
  for (const key of keys) {
    const v = payload[key];
    if (Array.isArray(v)) return v.map(String).map((x) => x.trim()).filter(Boolean);
    if (typeof v === 'string' && v.trim()) {
      return v
        .split('\n')
        .map((x) => x.trim())
        .filter(Boolean);
    }
  }
  return [];
}

function normalizeBlockType(raw: string, payload: Record<string, unknown>): CatalogBlockType {
  const normalized = normalizeCatalogBlockType(raw);
  if (normalized !== 'unknown') return normalized;
  const hasRichText = !!getString(payload, 'body_md', 'body', 'text', 'content');
  const hasMedia = !!getString(payload, 'imageUrl', 'image_url', 'src', 'url', 'embedUrl');
  const key = raw.trim().toLowerCase();
  if (hasMedia && key.includes('image')) return 'image';
  if (hasMedia && key.includes('video')) return 'video';
  if (hasMedia && key.includes('audio')) return 'audio';
  if (hasRichText) return 'paragraph';
  return 'unknown';
}

export function BlockRenderer(props: {
  block: BlockPayload;
  mode: DisplayMode;
  glossaryTerms?: Set<string>;
  audienceTag?: string | null;
  activePerspective?: PerspectiveMode;
}) {
  const { blockType, payload } = props.block;
  const canonicalType = normalizeBlockType(blockType, payload);
  const templateKind: BlockTemplateKind = templateForBlockType(canonicalType);
  const mode = props.mode;
  const audienceTag = normalizeAudience(props.audienceTag);
  const activePerspective = props.activePerspective ?? 'all';
  const payloadAudienceTags = Array.isArray(payload.audience_tags)
    ? (payload.audience_tags as unknown[]).map(normalizeAudience).filter(Boolean)
    : [];
  const excludeAudienceTags = Array.isArray(payload.exclude_audience_tags)
    ? (payload.exclude_audience_tags as unknown[]).map(normalizeAudience).filter(Boolean)
    : [];
  const minMode = typeof payload.min_mode === 'string' ? payload.min_mode : null;
  const maxMode = typeof payload.max_mode === 'string' ? payload.max_mode : null;

  if (payloadAudienceTags.length && (!audienceTag || !payloadAudienceTags.includes(audienceTag))) {
    return null;
  }

  if (audienceTag && excludeAudienceTags.includes(audienceTag)) {
    return null;
  }

  if (minMode && depthRank(mode) < depthRank(minMode as DisplayMode)) {
    return null;
  }

  if (maxMode && depthRank(mode) > depthRank(maxMode as DisplayMode)) {
    return null;
  }

  // Perspective filtering:
  // - 'none' => hide any perspective-tagged blocks (core only)
  // - any other string => show only blocks tagged with that perspective
  // - 'all' => show everything (admin/advanced view)
  if (activePerspective === 'none' && props.block.perspectives?.length) {
    return null;
  }

  if (activePerspective !== 'all' && activePerspective !== 'none' && props.block.perspectives?.length) {
    const tags = props.block.perspectives.map((p) => p.trim().toLowerCase()).filter(Boolean);
    if (tags.length && !tags.includes(String(activePerspective).toLowerCase())) {
      return null;
    }
  }

  /* --- Compare lens: only debate-relevant blocks --- */
  if (mode === 'compare') {
    if (!['criticism_block', 'response_block', 'compare_block', 'question_frame', 'short_answer'].includes(canonicalType)) {
      return null;
    }
  } else {
    /* --- Short layer: essentials only --- */
    if (mode === 'simple') {
      if (
        [
          'long_explanation',
          'reflection_prompt',
          'next_step_card',
          'criticism_block',
          'response_block',
          'historical_note',
          'scholar_view',
          'ayah_block',
          'hadith_block',
          'glossary_term',
          'books_body',
        ].includes(canonicalType)
      ) {
        return null;
      }
    }
    /* --- Full layer: hide advanced / study-only block types --- */
    if (mode === 'full') {
      // Full explanation should still include core sources (Qur'an + Hadith).
      if (['scholar_view', 'historical_note'].includes(canonicalType)) {
        return null;
      }
    }
  }

  if (canonicalType === 'books_body') {
    const studyOnly = payload.studyOnly === true;
    if (studyOnly && mode !== 'study') return null;
    if (mode === 'simple') return null;
    const blocks = Array.isArray(payload.blocks) ? payload.blocks : [];
    if (!blocks.length) return null;
    const intro = typeof payload.intro === 'string' ? payload.intro : null;
    const density = mode === 'full' ? 'compact' : 'expanded';
    return (
      <section className="block block-books card" data-wi-block-type="books_body">
        <BooksBlocks blocks={blocks} intro={intro} density={density} />
      </section>
    );
  }

  const renderBooksSingle = (raw: Record<string, unknown>) => {
    const density = mode === 'full' ? 'compact' : 'expanded';
    return (
      <section className={`block block-books card block-template-${templateKind}`} data-wi-block-type={canonicalType}>
        <BooksBlocks blocks={[raw]} density={density} />
      </section>
    );
  };

  switch (canonicalType) {
    case 'question_frame':
      return (
      <section className={`block block-frame card block-template-${templateKind}`} data-wi-block-type={canonicalType}>
          <h2 className="block-title">{(payload.title as string) ?? 'Framing'}</h2>
          <Md>{String(payload.body ?? '')}</Md>
          {Array.isArray(payload.misunderstandings) ? (
            <ul className="muted">
              {(payload.misunderstandings as string[]).map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          ) : null}
        </section>
      );
    case 'audience_note': {
      const title = typeof payload.title === 'string' ? payload.title : 'For your background';
      const label =
        typeof payload.audience_label === 'string'
          ? payload.audience_label
          : payloadAudienceTags[0]
            ? `For ${payloadAudienceTags[0]} readers`
            : 'Personalized note';
      return (
        <section className={`block block-frame card block-template-${templateKind}`} data-wi-block-type={canonicalType}>
          <h3 className="eyebrow">{label}</h3>
          <h2 className="block-title">{title}</h2>
          <Md>{String(payload.body_md ?? payload.body ?? '')}</Md>
        </section>
      );
    }
    case 'short_answer':
      return (
        <section className={`block block-short card block-template-${templateKind}`} data-wi-block-type={canonicalType}>
          <h2 className="block-title">{(payload.title as string) ?? 'Concise orientation'}</h2>
          <Md>{String(payload.body_md ?? '')}</Md>
        </section>
      );
    case 'long_explanation':
      return renderBooksSingle({
        type: 'toggle',
        text: `${getString(payload, 'title') || 'Deeper explanation'}\n${getString(payload, 'body_md', 'body', 'text')}`,
      });
    case 'scholar_view':
      return (
        <section className={`block block-scholar card block-template-${templateKind}`} data-wi-block-type={canonicalType}>
          <h3 className="eyebrow">Scholarly nuance</h3>
          <h2 className="block-title">{(payload.title as string) ?? 'Scholarly discussion'}</h2>
          <Md>{String(payload.body_md ?? '')}</Md>
        </section>
      );
    case 'historical_note':
      return (
        <section className={`block block-historical card block-template-${templateKind}`} data-wi-block-type={canonicalType}>
          <h3 className="eyebrow">Historical context</h3>
          <h2 className="block-title">{(payload.title as string) ?? 'Historical note'}</h2>
          <Md>{String(payload.body_md ?? '')}</Md>
        </section>
      );
    case 'ayah_block':
      return (
        <section className={`block block-ayah card block-template-${templateKind}`} data-wi-block-type={canonicalType}>
          <h3 className="eyebrow">Qur’anic reference</h3>
          {typeof payload.arabic === 'string' && payload.arabic.trim() ? (
            <p className="wi-ayah-arabic" dir="rtl" lang="ar">
              {payload.arabic.trim()}
            </p>
          ) : null}
          <Md>{String(payload.body_md ?? payload.translation_md ?? '')}</Md>
          {payload.citation ? <p className="muted small-print">{String(payload.citation)}</p> : null}
        </section>
      );
    case 'hadith_block':
      return (
        <section className={`block block-hadith card block-template-${templateKind}`} data-wi-block-type={canonicalType}>
          <h3 className="eyebrow">Hadith</h3>
          <Md>{String(payload.body_md ?? '')}</Md>
        </section>
      );
    case 'criticism_block':
      return (
        <section className={`block block-criticism card block-template-${templateKind}`} data-wi-block-type={canonicalType}>
          <h3 className="eyebrow">Common criticism</h3>
          <p className="criticism-label">{(payload.label as string) ?? 'Objection'}</p>
          <Md>{String(payload.body_md ?? '')}</Md>
        </section>
      );
    case 'response_block':
      return (
        <section className={`block block-response card block-template-${templateKind}`} data-wi-block-type={canonicalType}>
          <h3 className="eyebrow">Serious response</h3>
          <p className="response-label">{(payload.label as string) ?? 'Response'}</p>
          <Md>{String(payload.body_md ?? '')}</Md>
        </section>
      );
    case 'reflection_prompt':
      return renderBooksSingle({
        type: 'callout',
        variant: 'info',
        text: `Reflection\n${getString(payload, 'prompt', 'body_md', 'body', 'text')}`,
      });
    case 'next_step_card':
      return (
        <section className={`block block-next card block-template-${templateKind}`} data-wi-block-type={canonicalType}>
          <h3 className="eyebrow">{(payload.title as string) ?? 'Next steps'}</h3>
          <ul>
            {Array.isArray(payload.links)
              ? (payload.links as { label: string; slug: string | null }[]).map((l, i) => (
                  <li key={i}>
                    {l.slug ? <Link to={`/m/${l.slug}`}>{l.label}</Link> : <span>{l.label}</span>}
                  </li>
                ))
              : null}
          </ul>
        </section>
      );
    case 'callout_warning':
      return renderBooksSingle({
        type: 'callout',
        variant: 'warning',
        text: getString(payload, 'body_md', 'body', 'text'),
      });
    case 'glossary_term':
      return (
        <aside className={`block block-glossary card wi-block-glossary block-template-${templateKind}`} aria-label="Glossary" data-wi-block-type={canonicalType}>
          <span className="wi-block-glossary__badge" aria-hidden>
            <span className="wi-block-glossary__dot" />
            Term
          </span>
          <dl className="wi-block-glossary__dl">
            <dt>{formatInline(String(payload.term ?? ''))}</dt>
            <dd className="muted">{formatInline(String(payload.definition ?? ''))}</dd>
          </dl>
        </aside>
      );
    case 'quote_block':
      return renderBooksSingle({
        type: 'quote',
        text: [getString(payload, 'quote', 'body_md', 'body'), getString(payload, 'attribution', 'source')]
          .filter(Boolean)
          .join('\n- '),
      });
    case 'faq_item':
      return renderBooksSingle({
        type: 'toggle',
        text: `${getString(payload, 'question', 'title') || 'Question'}\n${getString(payload, 'answer_md', 'body_md', 'body')}`,
      });
    case 'heading':
      return renderBooksSingle({
        type: 'heading_2',
        text: getString(payload, 'title', 'text', 'body', 'content') || 'Section',
      });
    case 'subheading':
      return renderBooksSingle({
        type: 'heading_3',
        text: getString(payload, 'title', 'text', 'body', 'content') || 'Subsection',
      });
    case 'paragraph':
      return renderBooksSingle({
        type: 'paragraph',
        text: getString(payload, 'body_md', 'body', 'text', 'content'),
        rich: true,
      });
    case 'list':
    case 'ordered_list':
    case 'checklist': {
      const items = getArray(payload, 'items', 'body', 'body_md', 'text');
      if (!items.length) return null;
      return renderBooksSingle({
        type: canonicalType === 'ordered_list' ? 'numbered_list' : canonicalType === 'checklist' ? 'checklist' : 'bulleted_list',
        text: items.join('\n'),
      });
    }
    case 'image': {
      const src = getString(payload, 'imageUrl', 'image_url', 'src', 'url');
      if (!src) return null;
      const alt = getString(payload, 'alt', 'caption', 'title') || 'Image';
      const caption = getString(payload, 'caption');
      return renderBooksSingle({
        type: 'image',
        imageUrl: src,
        alt,
        caption,
      });
    }
    case 'video':
      return renderBooksSingle({
        type: 'video_embed',
        embedUrl: getString(payload, 'embedUrl', 'url', 'body_md', 'body'),
      });
    case 'audio':
      return renderBooksSingle({
        type: 'audio',
        url: getString(payload, 'audioUrl', 'url', 'body_md', 'body'),
      });
    case 'table':
      return renderBooksSingle({
        type: 'table',
        text: getString(payload, 'body_md', 'body', 'text'),
      });
    case 'code':
      return renderBooksSingle({
        type: 'code',
        text: getString(payload, 'code', 'body', 'body_md', 'text'),
      });
    case 'divider':
      return renderBooksSingle({ type: 'divider' });
    case 'toggle': {
      const title = getString(payload, 'title', 'summary') || 'Details';
      const body = getString(payload, 'body_md', 'body', 'text');
      return renderBooksSingle({
        type: 'toggle',
        text: body ? `${title}\n${body}` : title,
      });
    }
    case 'timeline':
      return (
        <section className={`block block-generic card block-template-${templateKind}`} data-wi-block-type={canonicalType}>
          <h3 className="eyebrow">Timeline</h3>
          <Md>{getString(payload, 'body_md', 'body', 'text')}</Md>
        </section>
      );
    case 'stat':
      return (
        <section className={`block block-generic card block-template-${templateKind}`} data-wi-block-type={canonicalType}>
          <h3 className="eyebrow">{getString(payload, 'label', 'title') || 'Key fact'}</h3>
          <p className="block-title">{formatInline(getString(payload, 'value', 'body', 'text'))}</p>
        </section>
      );
    case 'reference_list': {
      const refs = getArray(payload, 'references', 'items', 'body', 'text');
      return renderBooksSingle({
        type: 'bulleted_list',
        text: refs.join('\n'),
      });
    }
    case 'footnote':
      return renderBooksSingle({
        type: 'footnote',
        text: getString(payload, 'body_md', 'body', 'text'),
      });
    case 'compare_block':
      return (
        <section className={`block block-generic card block-template-${templateKind}`} data-wi-block-type={canonicalType}>
          <h3 className="eyebrow">{getString(payload, 'title') || 'Comparison'}</h3>
          <Md>{getString(payload, 'body_md', 'body', 'text')}</Md>
        </section>
      );
    default:
      return (
        <section className="block block-generic card" data-wi-block-type={canonicalType}>
          <h3 className="eyebrow">Additional context</h3>
          <Md>{getString(payload, 'body_md', 'body', 'text', 'content')}</Md>
          {mode === 'study' && import.meta.env.DEV ? (
            <details>
              <summary>{blockType}</summary>
              <pre className="payload-debug">{JSON.stringify(payload, null, 2)}</pre>
            </details>
          ) : null}
        </section>
      );
  }
}
